import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton, Tooltip } from '@mui/material';

import {
  FaIcon,
  If,
  MDBox,
  MDButton,
  MDTypography,
  ShareChartModal
} from '@components/index';

import { ChartDetails } from '@DStypes/chart';
import ChartDetailsModal from '../ChartDetailsModal';
import { useLazyGetChartLinkQuery } from '@api/chart';
import { RootState } from '@store/store';
import FullPageLoader from '@components/FullPageLoader';
import { getPageName } from '@utils/common';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  ADD_CHART,
  PAGE_VISITED,
  CLICKED_SHARE_CHART,
  CLICKED_VIEW_CLEANED_DATA,
  CLICKED_SHOW_INSIGHT
} from '@constants/eventNames';
import { sendGAData } from '@utils/googleAnalytics';
import useCustomNavigate from '@hooks/useCustomNavigate';
import useCharts from '@hooks/useCharts';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';
import { FeatureTypes } from '@constants/common';

const ChartHeader = ({
  projectId,
  setOpenCleanActionsModal,
  setOpenChartInsightsModal,
  showRedFlag,
  setShowRedFlag
}: {
  projectId: string;
  setOpenCleanActionsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenChartInsightsModal: React.Dispatch<React.SetStateAction<boolean>>;
  showRedFlag: boolean;
  setShowRedFlag: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { chartId } = useParams();
  const { onUpdateChart } = useCharts({ chartId: chartId });
  const navigate = useCustomNavigate();
  const [showEditIcon, setShowEditIcon] = useState(false);

  const [isEditToggled, setIsEditToggled] = useState<boolean>(false);
  const [shareChartModalOpen, setShareChartModalOpen] =
    useState<boolean>(false);

  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const isChartInsightsEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.chartInsights
  );

  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const [getChartLink, { data: chartLink, isLoading, isFetching }] =
    useLazyGetChartLinkQuery();

  const onUpdateChartDetails = (chartDetails: ChartDetails) => {
    onUpdateChart(chartDetails, currentChartData.rating);
    setIsEditToggled(false);
  };

  useEffect(() => {
    if (chartLink) navigator.clipboard.writeText(chartLink as string);
  }, [isLoading, isFetching]);

  return (
    <>
      <If condition={!!currentChartData.chart_title}>
        <MDBox
          display='flex'
          flexDirection='column'
          paddingBottom={1}
          px={1}
          mt='1.3rem'
        >
          <MDBox
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            flexWrap='nowrap'
          >
            <MDBox
              display='flex'
              alignItems='center'
              onMouseOver={() => setShowEditIcon(true)}
              onMouseLeave={() => setShowEditIcon(false)}
            >
              <Tooltip
                title={
                  currentChartData.chart_title &&
                  currentChartData.chart_title.length <= 50
                    ? ''
                    : currentChartData.chart_title || ''
                }
                placement='top'
              >
                <MDBox display='flex' alignItems='center' columnGap={0.5}>
                  <MDTypography
                    variant='h3'
                    textGradient
                    color='secondary'
                    onClick={() => setIsEditToggled(true)}
                    sx={{
                      cursor: 'pointer',
                      whiteSpace: 'pre-wrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maxWidth: '70vw'
                    }}
                  >
                    {currentChartData?.chart_title || ''}
                    <Tooltip
                      title={`Edit ${
                        currentChartData.config.chart_type === 'KPI'
                          ? 'KPI'
                          : 'Chart'
                      } Name & Description`}
                      placement='right'
                    >
                      <span>
                        <FaIcon
                          icon='pencil'
                          typographyProps={{
                            textGradient: true,
                            color: 'dark',
                            variant: 'lg'
                          }}
                          onClick={() => setIsEditToggled(true)}
                          style={{
                            marginLeft: '5px',
                            opacity: showEditIcon ? 1 : 0,
                            alignSelf: 'flex-end'
                          }}
                        />
                      </span>
                    </Tooltip>
                  </MDTypography>
                </MDBox>
              </Tooltip>
            </MDBox>
            <MDBox
              display='flex'
              justifyContent='space-between'
              alignContent='center'
              sx={{ width: { xs: '100%', sm: 'fit-content' } }}
            >
              <MDBox display={{ xs: 'none', md: 'flex' }}>
                <Tooltip
                  title='Click to generate automated insights'
                  placement='top'
                >
                  <IconButton
                    onClick={async () => {
                      sendAmplitudeData(CLICKED_SHOW_INSIGHT, {
                        type: 'Chart',
                        red_flag: showRedFlag
                      });
                      if (subscriptionFlag) {
                        // If feature not accessible by plan then return
                        const feature = isChartInsightsEnabledByPlan
                          ? FeatureTypes.MAX_MONTHLY_INSIGHTS_LIMIT
                          : FeatureTypes.CHART_INSIGHTS;

                        const response = await getFeatureAccess(feature);

                        if (response?.isSuccess)
                          setOpenChartInsightsModal(true);
                      }
                    }}
                    sx={{ mr: '10px', pl: 0 }}
                  >
                    <FaIcon
                      icon='lightbulb-on'
                      typographyProps={{ color: 'primary', variant: 'xl' }}
                    />
                  </IconButton>
                </Tooltip>
              </MDBox>
              <MDBox display={{ xs: 'none', md: 'flex' }}>
                <Tooltip title='View File information' placement='top'>
                  <IconButton
                    onClick={() => {
                      setOpenCleanActionsModal(true);
                      sendAmplitudeData(CLICKED_VIEW_CLEANED_DATA);
                    }}
                    sx={{ mr: '10px', pl: 0 }}
                  >
                    <FaIcon
                      icon='table-layout'
                      typographyProps={{ color: 'dark', variant: 'xl' }}
                    />
                  </IconButton>
                </Tooltip>
              </MDBox>
              <MDButton
                color='light'
                size='small'
                sx={{
                  mr: '10px',
                  p: 0.8
                }}
                onClick={() => {
                  sendAmplitudeData(ADD_CHART, {
                    'Go Auto Enabled':
                      currentChartData?.chart_project_auto_charts_executed
                        ? 'Yes'
                        : 'No'
                  });
                  sendGAData(ADD_CHART, {
                    'Go Auto Enabled':
                      currentChartData?.chart_project_auto_charts_executed
                        ? 'Yes'
                        : 'No'
                  });
                  navigate(`/chart/${projectId}/new`, {
                    state: { prevEditChartId: chartId }
                  });
                }}
              >
                <FaIcon
                  icon='plus-square'
                  typographyProps={{ color: 'primary', variant: 'xl' }}
                />
                <MDTypography
                  textTransform='capitalize'
                  variant='sm'
                  color='info'
                  fontWeight='bold'
                  pl={1}
                >
                  NEW CHART
                </MDTypography>
              </MDButton>
              <Tooltip
                title={`Share this ${
                  currentChartData.config.chart_type === 'KPI' ? 'KPI' : 'chart'
                }`}
                placement='top'
              >
                <MDButton
                  color='primary'
                  size='small'
                  variant='gradient'
                  sx={{ p: 0.8 }}
                  onClick={() => {
                    setShareChartModalOpen(true);
                    sendAmplitudeData(CLICKED_SHARE_CHART);
                    sendGAData(CLICKED_SHARE_CHART);
                    sendAmplitudeData(PAGE_VISITED, {
                      'Page Name': 'Share Chart Modal',
                      Navigation: getPageName()
                    });
                    sendGAData(PAGE_VISITED, {
                      'Page Name': 'Share Chart Modal',
                      Navigation: getPageName()
                    });
                  }}
                >
                  <FaIcon
                    icon='share'
                    typographyProps={{ color: 'white', variant: 'xl' }}
                  />
                  <MDTypography
                    textTransform='capitalize'
                    variant='sm'
                    color='white'
                    fontWeight='bold'
                    pl={1}
                  >
                    SHARE
                  </MDTypography>
                </MDButton>
              </Tooltip>
            </MDBox>
          </MDBox>
          <MDBox
            display='flex'
            flexDirection='row'
            alignItems='center'
            maxWidth='92vw'
            mt={0.5}
          >
            <Tooltip
              title={currentChartData.chart_short_desc || ''}
              placement='bottom-start'
            >
              <MDTypography
                width='fit-content'
                variant='sm'
                color='info'
                sx={{
                  whiteSpace: 'pre-wrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  opacity: currentChartData.chart_short_desc ? 1 : 0.5,
                  cursor: 'pointer',
                  wordBreak: 'normal',
                  fontWeight: 600
                }}
                onClick={() => {
                  setIsEditToggled(true);
                  setShowRedFlag(false);
                }}
              >
                {currentChartData?.chart_short_desc ||
                  `Add a description to the ${
                    currentChartData.config.chart_type === 'KPI'
                      ? 'KPI'
                      : 'chart'
                  } (it will appear in downloaded ${
                    currentChartData.config.chart_type === 'KPI'
                      ? 'KPI'
                      : 'chart'
                  } & report)`}
              </MDTypography>
            </Tooltip>
            {showRedFlag && (
              <IconButton
                sx={{ transform: 'rotate(180deg)', p: 0, mt: 1, ml: 0.5 }}
                onClick={() => setShowRedFlag(false)}
              >
                <Tooltip
                  title={
                    <>
                      {`Your description may need an update as you've modified certain parameters of this chart. You might want to review, and/or view updated insights by DataSquirrel by selecting the  `}
                      <FaIcon
                        icon='lightbulb-on'
                        typographyProps={{ color: 'white', variant: 'md' }}
                        style={{ pointerEvents: 'none', marginLeft: 5 }}
                      />
                    </>
                  }
                >
                  <span>
                    <FaIcon
                      icon='play'
                      typographyProps={{ variant: 'sm', color: 'error' }}
                    />
                  </span>
                </Tooltip>
              </IconButton>
            )}
          </MDBox>
        </MDBox>
        {isEditToggled && (
          <ChartDetailsModal
            open={isEditToggled}
            onClose={() => setIsEditToggled(false)}
            onUpdateChartDetails={onUpdateChartDetails}
          />
        )}
        <ShareChartModal
          open={shareChartModalOpen}
          onClose={() => setShareChartModalOpen(false)}
          onClickGetLink={() => getChartLink(chartId as string)}
          chartLink={chartLink}
          chartId={chartId}
        />
      </If>
      <If condition={!currentChartData.chart_title}>
        <FullPageLoader height='100%' />
      </If>
    </>
  );
};

export default ChartHeader;
